import { useCallback, useEffect, useMemo, useState } from "react";

import {
  CellContext,
  createColumnHelper,
  DisplayColumnDef,
} from "@tanstack/react-table";
import { capitalize } from "lodash-es";

import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { FeatureResponse, FeatureType } from "src/clients/features-control";
import { EntityFeatureValue } from "src/clients/features-retrieve";
import { DecisionEnvironment } from "src/clients/jobs";
import { Icon } from "src/design-system/Icon";
import {
  renderFeatureName,
  renderFeatureValue,
} from "src/entities/entityView/Features";
import { buildEntityColumns } from "src/entities/entityView/RelatedEntities";
import { getSchemaIcon } from "src/entities/entityView/utils";
import {
  EntitySchemaResource,
  FlatEntityResource,
  useEntities,
} from "src/entities/queries";
import { useEntityFeatures } from "src/featureCatalogue/queries";
import { Environment, useEnvironment } from "src/hooks/useEnvironment";
import {
  SourceCountDataConfig,
  SourcePreviewDataConfig,
} from "src/jobs/common/entitySource/types";

type FlatEntityResourceWithFeatures = FlatEntityResource & {
  isFeatureLoading: boolean;
  features: {
    [key: string]: EntityFeatureValue["value"];
  };
};

const buildColumns = (
  schema: EntitySchemaResource,
  features: FeatureResponse[],
  environment: Environment,
): ReturnType<typeof buildEntityColumns> => {
  const helper = createColumnHelper<FlatEntityResourceWithFeatures>();

  const indexColumn = helper.accessor((_row, index) => index + 1, {
    id: "index",
    header: (info) =>
      info.header.depth === 2 ? null : <Header info={info}>#</Header>,
    cell: (info) => <Cell info={info}>{info.getValue()}</Cell>,
  });

  const featureColumns = features.map((feature) => {
    return helper.accessor((row) => row.features[feature.key], {
      id: `features.${feature.key}`,
      meta: {
        displayName: feature.name,
      },
      header: (info) =>
        renderFeatureName(
          feature.name,
          feature.feature_type ?? FeatureType.STRING,
          info as unknown as CellContext<EntityFeatureValue, string>,
        ),
      cell: (info) =>
        info.row.original.isFeatureLoading ? (
          <div className="flex items-center justify-end">
            <SkeletonPlaceholder height="h-4" width="w-14" />
          </div>
        ) : (
          renderFeatureValue(
            info.getValue() ?? null,
            info as unknown as CellContext<
              EntityFeatureValue,
              EntityFeatureValue["value"]
            >,
          )
        ),
    });
  });

  const entitySubColumns = buildEntityColumns(schema, environment);

  const entityColumns = helper.group({
    id: "entity",
    header: (info) => (
      <Header info={info}>
        <div className="flex h-full w-full items-center gap-x-1 bg-gray-50">
          <Icon icon={getSchemaIcon(schema)} size="2xs" />
          <p className="font-inter-medium-12px text-gray-800">
            {schema._display_name_plural}
          </p>
        </div>
      </Header>
    ),
    columns: [
      ...entitySubColumns.slice(0, -1),
      ...featureColumns,
      ...entitySubColumns.slice(-1),
    ] as DisplayColumnDef<FlatEntityResourceWithFeatures, unknown>[],
  });

  return [indexColumn, entityColumns] as DisplayColumnDef<
    FlatEntityResource,
    unknown
  >[];
};

const PreviewMetaInfo = ({
  previewCount,
  countDataConfig,
  environment,
}: {
  previewCount: number;
  countDataConfig: SourceCountDataConfig;
  environment: DecisionEnvironment;
}) => {
  return (
    <div className="flex w-full justify-between">
      <div className="flex items-center gap-x-1.5 font-inter-medium-12px text-gray-500">
        <span>Showing preview of</span>
        <Pill variant="gray">
          <Pill.Text>{previewCount}</Pill.Text>
        </Pill>
        <span>from</span>
        {countDataConfig.isPending ? (
          <SkeletonPlaceholder height="h-4" width="w-8" />
        ) : (
          <Pill variant="gray">
            <Pill.Text>{countDataConfig.data?.count}</Pill.Text>
          </Pill>
        )}
        <span>matches found.</span>
      </div>

      <Pill data-loc="environment-pill">
        <Pill.Text>{capitalize(environment)}</Pill.Text>
      </Pill>
    </div>
  );
};

type Props = {
  previewDataConfig: SourcePreviewDataConfig;
  countDataConfig: SourceCountDataConfig;
  entitySchema: EntitySchemaResource;
  features: FeatureResponse[];
};

export const EntitiesPreview: React.FC<Props> = ({
  previewDataConfig: {
    data: { results },
    environment,
  },
  countDataConfig,
  entitySchema,
  features,
}) => {
  const entityIds = useMemo(() => {
    return results.map((result) => Object.values(result)[0] as string);
  }, [results]);

  const {
    data: entityPages,
    isLoading: isLoadingEntities,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useEntities({
    env: environment as Environment,
    schema: entitySchema._id,
    limit: 100,
    filters: {
      "filter[id][one_of]": entityIds.join(","),
    },
  });

  const [, setEnv] = useEnvironment();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadedEntityIds, setLoadedEntityIds] = useState<string[]>([]);
  const [featuresByEntityId, setFeaturesByEntityId] = useState<
    Record<string, FlatEntityResourceWithFeatures["features"]>
  >({});

  const entities = useMemo(() => {
    return entityPages?.pages
      .flatMap((page) => page.entities)
      .map(
        ({ properties, ...entity }) =>
          ({
            ...entity,
            ...properties,
            isFeatureLoading: !featuresByEntityId[entity._id],
            features: featuresByEntityId[entity._id] ?? {},
          }) as unknown as FlatEntityResourceWithFeatures,
      );
  }, [entityPages, featuresByEntityId]);

  const columns = useMemo(() => {
    if (entitySchema) {
      return buildColumns(entitySchema, features, environment as Environment);
    }

    return [];
  }, [entitySchema, features, environment]);

  const tableActions = useMemo(
    () => ({
      setSelectedId,
      getSelectedId: () => selectedId,
    }),
    [selectedId],
  );

  useEffect(() => {
    setEnv(environment === DecisionEnvironment.LIVE ? "live" : "sandbox");
  }, [environment, setEnv]);

  const handleLoadFeatures = useCallback(
    (
      entityId: string,
      features: FlatEntityResourceWithFeatures["features"],
    ) => {
      setLoadedEntityIds((prev) =>
        prev.includes(entityId) ? prev : [...prev, entityId],
      );

      setFeaturesByEntityId((prev) => ({
        ...prev,
        [entityId]: features,
      }));
    },
    [],
  );

  return (
    <div className="flex h-full flex-col gap-y-3">
      <PreviewMetaInfo
        countDataConfig={countDataConfig}
        environment={environment}
        previewCount={entityIds.length}
      />
      <TableComp
        actions={tableActions}
        columns={columns}
        data={entities ?? []}
        dataLoc="entities-preview-table"
        headerPropsGetter={() => ({
          classNameOverrides: "bg-gray-50",
        })}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoadingEntities}
        rowPropsGetter={(row) => ({
          onClick: () => {
            tableActions.setSelectedId((prev) =>
              prev === row.original._id ? null : row.original._id,
            );
          },
        })}
        variant="compact"
        onScrolledToTheBottom={() => {
          if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
      />
      {entitySchema &&
        entityIds
          .slice(0, loadedEntityIds.length + 1)
          .map((entityId) => (
            <LoadFeatures
              key={entityId}
              entityId={entityId}
              env={environment}
              schemaName={entitySchema._id}
              onSuccess={handleLoadFeatures}
            />
          ))}
    </div>
  );
};

type LoadFeaturesProps = {
  schemaName: string;
  env: DecisionEnvironment;
  entityId: string;
  onSuccess: (
    entityId: string,
    features: FlatEntityResourceWithFeatures["features"],
  ) => void;
};

const LoadFeatures: React.FC<LoadFeaturesProps> = ({
  onSuccess,
  schemaName,
  env,
  entityId,
}) => {
  const { data: features } = useEntityFeatures(
    env as Environment,
    entityId,
    schemaName,
    { enabled: true, staleTime: Infinity },
  );

  useEffect(() => {
    const featuresData = features?.data;
    if (featuresData) {
      const featuresByKey = featuresData.reduce(
        (acc, feature) => {
          acc[feature.key] = feature.value;
          return acc;
        },
        {} as FlatEntityResourceWithFeatures["features"],
      );

      onSuccess(entityId, featuresByKey);
    }
  }, [features?.data, entityId, onSuccess]);

  return null;
};
